import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Car, Pencil, Trash2, UserPlus, Calendar, ShieldCheck, Wrench, CheckSquare, Navigation, Upload, FileText, Eye, X } from "lucide-react";
import { useTableFilter, ExportBar } from "../components/TableExport";
import { Checkbox } from "../components/ui/checkbox";

const statusMap = { actif: "Actif", immobilise: "Immobilise", repos: "Repos" };
const statusClass = { actif: "status-actif", immobilise: "status-immobilise", repos: "status-repos" };

const ACCESSOIRES_LIST = [
  "Extincteur",
  "Pneu de secours",
  "Cric",
  "Cle de roue",
  "Gilet de securite",
  "Triangle de signalisation",
  "Kit de secours",
  "Documents du vehicule (VT, Ass, C. Grise)",
  "Autres",
];

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [gestionnaires, setGestionnaires] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [form, setForm] = useState({ marque: "", modele: "", immatriculation: "", annee: "", couleur: "", investisseur_id: "", gestionnaire_id: "", prix_acquisition: "", recette_journaliere: "", accessoires: [], accessoires_autres: "", date_visite_technique: "", date_assurance: "", date_entretien: "", date_credit_gps: "" });
  const [assignForm, setAssignForm] = useState({ chauffeur_id: "", jours_travail: [], jour_non_remunere: "", recette_par_jour: "", plage_horaire_debut: "06:00", plage_horaire_fin: "22:00", heure_paiement: "14:00", date_debut: "" });
  const [loading, setLoading] = useState(true);
  const [showDocs, setShowDocs] = useState(null); // vehicle id
  const [vehicleDocs, setVehicleDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editAssignment, setEditAssignment] = useState(null);
  const [editAssignForm, setEditAssignForm] = useState({ jours_travail: [], jour_non_remunere: "", recette_par_jour: "", plage_horaire_debut: "06:00", plage_horaire_fin: "22:00", heure_paiement: "14:00" });

  const jours = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];

  const fetchData = async () => {
    try {
      const [vRes, iRes, dRes, aRes, gRes] = await Promise.all([
        api.get("/vehicles"), api.get("/users?role=investisseur"), api.get("/users?role=chauffeur"), api.get("/assignments"), api.get("/users?role=gestionnaire")
      ]);
      setVehicles(vRes.data); setInvestors(iRes.data); setDrivers(dRes.data); setAssignments(aRes.data); setGestionnaires(gRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const DOC_LABELS = { carte_grise: "Carte grise", visite_technique: "Visite technique", assurance: "Assurance" };

  const openDocs = async (vehicleId) => {
    setShowDocs(vehicleId);
    try {
      const res = await api.get(`/vehicles/${vehicleId}/documents`);
      setVehicleDocs(res.data);
    } catch { setVehicleDocs([]); }
  };

  const handleDocUpload = async (vehicleId, docType, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/vehicles/${vehicleId}/documents/${docType}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${DOC_LABELS[docType]} uploade`);
      const res = await api.get(`/vehicles/${vehicleId}/documents`);
      setVehicleDocs(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'upload"); }
    finally { setUploading(false); }
  };

  const handleDocDelete = async (vehicleId, docId) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete(`/vehicles/${vehicleId}/documents/${docId}`);
      toast.success("Document supprime");
      setVehicleDocs(prev => prev.filter(d => d.id !== docId));
    } catch (e) { toast.error("Erreur"); }
  };

  const getDocUrl = (storagePath) => {
    const token = localStorage.getItem("vtc_token");
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${storagePath}?auth=${token}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, annee: form.annee ? parseInt(form.annee) : null, prix_acquisition: parseFloat(form.prix_acquisition || 0), recette_journaliere: parseFloat(form.recette_journaliere || 0), accessoires_autres: form.accessoires_autres || "" };
      if (selectedVehicle) {
        await api.put(`/vehicles/${selectedVehicle.id}`, payload);
        toast.success("Vehicule mis a jour");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Vehicule ajoute");
      }
      setShowAdd(false); setSelectedVehicle(null);
      setForm({ marque: "", modele: "", immatriculation: "", annee: "", couleur: "", investisseur_id: "", gestionnaire_id: "", prix_acquisition: "", recette_journaliere: "", accessoires: [], accessoires_autres: "", date_visite_technique: "", date_assurance: "", date_entretien: "", date_credit_gps: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      await api.post("/assignments", { ...assignForm, vehicule_id: selectedVehicle.id, recette_par_jour: parseFloat(assignForm.recette_par_jour || 0) });
      toast.success("Chauffeur affecte");
      setShowAssign(false); setSelectedVehicle(null);
      setAssignForm({ chauffeur_id: "", jours_travail: [], jour_non_remunere: "", recette_par_jour: "", plage_horaire_debut: "06:00", plage_horaire_fin: "22:00", heure_paiement: "14:00", date_debut: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce vehicule ?")) return;
    try { await api.delete(`/vehicles/${id}`); toast.success("Vehicule supprime"); fetchData(); } catch (e) { toast.error("Erreur"); }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm("Supprimer cette affectation ?")) return;
    try { await api.delete(`/assignments/${id}`); toast.success("Affectation supprimee"); fetchData(); } catch (e) { toast.error("Erreur"); }
  };

  const openEditAssignment = (a) => {
    setEditAssignment(a);
    setEditAssignForm({
      jours_travail: a.jours_travail || [],
      jour_non_remunere: a.jour_non_remunere || "",
      recette_par_jour: a.recette_par_jour || "",
      plage_horaire_debut: a.plage_horaire_debut || "06:00",
      plage_horaire_fin: a.plage_horaire_fin || "22:00",
      heure_paiement: a.heure_paiement || "14:00",
    });
  };

  const handleEditAssignment = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/assignments/${editAssignment.id}`, { ...editAssignForm, recette_par_jour: parseFloat(editAssignForm.recette_par_jour || 0) });
      toast.success("Affectation modifiee");
      setEditAssignment(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openEdit = (v) => {
    setSelectedVehicle(v);
    setForm({ marque: v.marque, modele: v.modele, immatriculation: v.immatriculation, annee: v.annee || "", couleur: v.couleur || "", investisseur_id: v.investisseur_id || "", gestionnaire_id: v.gestionnaire_id || "", prix_acquisition: v.prix_acquisition || "", recette_journaliere: v.recette_journaliere || "", accessoires: v.accessoires || [], accessoires_autres: v.accessoires_autres || "", date_visite_technique: v.date_visite_technique || "", date_assurance: v.date_assurance || "", date_entretien: v.date_entretien || "", date_credit_gps: v.date_credit_gps || "" });
    setShowAdd(true);
  };

  const getInvestorName = (id) => { const inv = investors.find(i => i.id === id); return inv ? `${inv.prenom} ${inv.nom}` : "-"; };
  const getGestionnaireName = (id) => { const g = gestionnaires.find(i => i.id === id); return g ? `${g.prenom} ${g.nom}` : "-"; };
  const getDriverName = (id) => { const d = drivers.find(dr => dr.id === id); return d ? `${d.prenom} ${d.nom}` : "-"; };

  const { filtered: filteredVehicles, searchTerm, setSearchTerm, filters, setFilter, resetFilters, hasActiveFilters } = useTableFilter(vehicles, {
    searchFields: ["marque", "modele", "immatriculation"],
    filterConfigs: [
      { key: "statut", label: "Tous statuts", options: [{ value: "actif", label: "Actif" }, { value: "immobilise", label: "Immobilise" }, { value: "repos", label: "Repos" }] }
    ]
  });

  const exportColumns = [
    { label: "Marque", key: "marque" },
    { label: "Modele", key: "modele" },
    { label: "Immatriculation", key: "immatriculation" },
    { label: "Annee", key: "annee" },
    { label: "Investisseur", accessor: r => getInvestorName(r.investisseur_id) },
    { label: "Gestionnaire", accessor: r => getGestionnaireName(r.gestionnaire_id) },
    { label: "Recette/jour", key: "recette_journaliere" },
    { label: "Statut", key: "statut" },
    { label: "Visite technique", key: "date_visite_technique" },
    { label: "Assurance", key: "date_assurance" },
    { label: "Entretien", key: "date_entretien" },
    { label: "Credit GPS", key: "date_credit_gps" },
  ];

  const formatDateEcheance = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T12:00:00");
    const now = new Date();
    const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
    const formatted = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    if (diff < 0) return { text: formatted, cls: "text-red-600 font-semibold", label: "Expire" };
    if (diff <= 15) return { text: formatted, cls: "text-amber-600 font-medium", label: `${diff}j` };
    if (diff <= 30) return { text: formatted, cls: "text-amber-500", label: `${diff}j` };
    return { text: formatted, cls: "text-slate-600", label: null };
  };

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div data-testid="vehicles-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Vehicules</h1>
          <p className="text-sm text-slate-500 mt-1">{vehicles.length} vehicule(s) dans la flotte</p>
        </div>
        <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => { setSelectedVehicle(null); setForm({ marque: "", modele: "", immatriculation: "", annee: "", couleur: "", investisseur_id: "", gestionnaire_id: "", prix_acquisition: "", recette_journaliere: "", accessoires: [], accessoires_autres: "", date_visite_technique: "", date_assurance: "", date_entretien: "", date_credit_gps: "" }); setShowAdd(true); }} data-testid="btn-add-vehicle">
          <Plus className="w-4 h-4 mr-2" />Ajouter
        </Button>
      </div>

      <ExportBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Rechercher marque, modele, immat..."
        filterConfigs={[{ key: "statut", label: "Tous statuts", options: [{ value: "actif", label: "Actif" }, { value: "immobilise", label: "Immobilise" }, { value: "repos", label: "Repos" }] }]}
        filters={filters} onFilterChange={setFilter} onReset={resetFilters} hasActiveFilters={hasActiveFilters}
        data={filteredVehicles} columns={exportColumns} filename="vehicules" title="Liste des vehicules"
      />

      <div className="table-container">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold text-slate-600">Vehicule</TableHead>
              <TableHead className="font-semibold text-slate-600">Immatriculation</TableHead>
              <TableHead className="font-semibold text-slate-600">Investisseur</TableHead>
              <TableHead className="font-semibold text-slate-600">Gestionnaire</TableHead>
              <TableHead className="font-semibold text-slate-600">Chauffeurs</TableHead>
              <TableHead className="font-semibold text-slate-600">Recette/jour</TableHead>
              <TableHead className="font-semibold text-slate-600">Echeances</TableHead>
              <TableHead className="font-semibold text-slate-600">Statut</TableHead>
              <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredVehicles.map((v) => {
              const vAssignments = assignments.filter(a => a.vehicule_id === v.id);
              return (
                <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`}>
                  <TableCell className="font-medium text-slate-900">{v.marque} {v.modele} {v.annee ? `(${v.annee})` : ""}</TableCell>
                  <TableCell className="text-slate-600">{v.immatriculation}</TableCell>
                  <TableCell className="text-slate-600">{getInvestorName(v.investisseur_id)}</TableCell>
                  <TableCell className="text-slate-600">{getGestionnaireName(v.gestionnaire_id)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {vAssignments.map(a => (
                        <div key={a.id} className="flex items-center gap-0.5" data-testid={`assignment-badge-${a.id}`}>
                          <Badge
                            variant="outline"
                            className="text-xs cursor-pointer hover:bg-blue-50 hover:border-[#0F52BA] transition-colors"
                            onClick={() => openEditAssignment(a)}
                            data-testid={`btn-edit-assign-${a.id}`}
                          >
                            {getDriverName(a.chauffeur_id)}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 text-red-400 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteAssignment(a.id)}
                            data-testid={`btn-delete-assign-${a.id}`}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      {vAssignments.length < 3 && (
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-[#0F52BA]" onClick={() => { setSelectedVehicle(v); setShowAssign(true); }} data-testid={`btn-assign-${v.id}`}>
                          <UserPlus className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{v.recette_journaliere || 0} Frc</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {[
                        { date: v.date_visite_technique, icon: Calendar, label: "VT" },
                        { date: v.date_assurance, icon: ShieldCheck, label: "Ass." },
                        { date: v.date_entretien, icon: Wrench, label: "Ent." },
                        { date: v.date_credit_gps, icon: Navigation, label: "GPS" },
                      ].map(({ date, icon: Icon, label }) => {
                        const info = formatDateEcheance(date);
                        if (!info) return <div key={label} className="flex items-center gap-1.5 text-xs text-slate-300"><Icon className="w-3 h-3" />{label}: -</div>;
                        return (
                          <div key={label} className={`flex items-center gap-1.5 text-xs ${info.cls}`}>
                            <Icon className="w-3 h-3" />
                            <span>{label}: {info.text}</span>
                            {info.label && <Badge variant="outline" className={`text-[9px] px-1 py-0 ${info.label === "Expire" ? "bg-red-50 text-red-600 border-red-200" : "bg-amber-50 text-amber-600 border-amber-200"}`}>{info.label}</Badge>}
                          </div>
                        );
                      })}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className={statusClass[v.statut] || ""}>{statusMap[v.statut] || v.statut}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDocs(v.id)} data-testid={`btn-docs-${v.id}`}><FileText className="w-4 h-4 text-[#0F52BA]" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(v)} data-testid={`btn-edit-${v.id}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(v.id)} data-testid={`btn-delete-${v.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredVehicles.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">Aucun vehicule</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Vehicle Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-dialog">
          <DialogHeader><DialogTitle className="font-heading">{selectedVehicle ? "Modifier le vehicule" : "Ajouter un vehicule"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Marque</Label><Input value={form.marque} onChange={e => setForm({...form, marque: e.target.value})} required data-testid="input-marque" /></div>
              <div><Label>Modele</Label><Input value={form.modele} onChange={e => setForm({...form, modele: e.target.value})} required data-testid="input-modele" /></div>
              <div><Label>Immatriculation</Label><Input value={form.immatriculation} onChange={e => setForm({...form, immatriculation: e.target.value})} required data-testid="input-immat" /></div>
              <div><Label>Annee</Label><Input type="number" value={form.annee} onChange={e => setForm({...form, annee: e.target.value})} data-testid="input-annee" /></div>
              <div><Label>Couleur</Label><Input value={form.couleur} onChange={e => setForm({...form, couleur: e.target.value})} data-testid="input-couleur" /></div>
              <div>
                <Label>Investisseur</Label>
                <Select value={form.investisseur_id} onValueChange={v => setForm({...form, investisseur_id: v})} data-testid="select-investor">
                  <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{investors.map(inv => <SelectItem key={inv.id} value={inv.id}>{inv.prenom} {inv.nom}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Gestionnaire</Label>
                <Select value={form.gestionnaire_id} onValueChange={v => setForm({...form, gestionnaire_id: v})} data-testid="select-gestionnaire">
                  <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{gestionnaires.map(g => <SelectItem key={g.id} value={g.id}>{g.prenom} {g.nom}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Prix acquisition (Frc)</Label><Input type="number" value={form.prix_acquisition} onChange={e => setForm({...form, prix_acquisition: e.target.value})} data-testid="input-prix-acq" /></div>
              <div><Label>Recette/jour (Frc)</Label><Input type="number" value={form.recette_journaliere} onChange={e => setForm({...form, recette_journaliere: e.target.value})} data-testid="input-recette-jour" /></div>
            </div>
            {/* Accessoires */}
            <div className="border-t pt-3 mt-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><CheckSquare className="w-3.5 h-3.5 text-[#0F52BA]" />Accessoires</p>
                <Button
                  type="button" variant="outline" size="sm" className="h-6 text-[11px] px-2"
                  onClick={() => setForm({...form, accessoires: form.accessoires.length === ACCESSOIRES_LIST.length ? [] : [...ACCESSOIRES_LIST]})}
                  data-testid="btn-toggle-all-accessoires"
                >
                  {form.accessoires.length === ACCESSOIRES_LIST.length ? "Tout decocher" : "Tout cocher"}
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {ACCESSOIRES_LIST.map(acc => acc === "Autres" ? (
                  <label key={acc} className="flex items-center gap-1.5 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 cursor-pointer text-xs" data-testid="acc-autres">
                    <Checkbox
                      className="h-3.5 w-3.5"
                      checked={!!form.accessoires_autres}
                      onCheckedChange={(checked) => { if (!checked) setForm({...form, accessoires_autres: ""}); }}
                    />
                    <Input
                      placeholder="Autres..."
                      className="h-5 text-xs border-0 p-0 focus-visible:ring-0 shadow-none bg-transparent"
                      value={form.accessoires_autres}
                      onChange={e => setForm({...form, accessoires_autres: e.target.value})}
                      data-testid="input-accessoires-autres"
                    />
                  </label>
                ) : (
                  <label key={acc} className="flex items-center gap-1.5 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 cursor-pointer text-xs" data-testid={`acc-${acc.replace(/[^a-zA-Z]/g, '-').toLowerCase()}`}>
                    <Checkbox
                      className="h-3.5 w-3.5"
                      checked={form.accessoires.includes(acc)}
                      onCheckedChange={(checked) => setForm({...form, accessoires: checked ? [...form.accessoires, acc] : form.accessoires.filter(a => a !== acc)})}
                    />
                    <span className="text-slate-600 leading-tight">{acc}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="border-t pt-4 mt-2">
              <p className="text-sm font-semibold text-slate-700 mb-3">Dates d'echeance</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#0F52BA]" />Visite technique</Label>
                  <Input type="date" value={form.date_visite_technique} onChange={e => setForm({...form, date_visite_technique: e.target.value})} data-testid="input-date-vt" />
                </div>
                <div>
                  <Label className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#0F52BA]" />Assurance</Label>
                  <Input type="date" value={form.date_assurance} onChange={e => setForm({...form, date_assurance: e.target.value})} data-testid="input-date-assurance" />
                </div>
                <div>
                  <Label className="flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5 text-[#0F52BA]" />Entretien</Label>
                  <Input type="date" value={form.date_entretien} onChange={e => setForm({...form, date_entretien: e.target.value})} data-testid="input-date-entretien" />
                </div>
                <div>
                  <Label className="flex items-center gap-1.5"><Navigation className="w-3.5 h-3.5 text-[#0F52BA]" />Credit GPS</Label>
                  <Input type="date" value={form.date_credit_gps} onChange={e => setForm({...form, date_credit_gps: e.target.value})} data-testid="input-date-credit-gps" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-vehicle">{selectedVehicle ? "Modifier" : "Ajouter"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Driver Dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="max-w-lg" data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="font-heading">Affecter un chauffeur a {selectedVehicle?.marque} {selectedVehicle?.modele}</DialogTitle></DialogHeader>
          <form onSubmit={handleAssign} className="space-y-4">
            <div>
              <Label>Chauffeur</Label>
              <Select value={assignForm.chauffeur_id} onValueChange={v => setAssignForm({...assignForm, chauffeur_id: v})}>
                <SelectTrigger data-testid="select-driver"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.prenom} {d.nom}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jours de travail</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant={assignForm.jours_travail.length === jours.length ? "default" : "outline"} className={`cursor-pointer ${assignForm.jours_travail.length === jours.length ? "bg-emerald-600" : ""}`}
                  onClick={() => setAssignForm({...assignForm, jours_travail: assignForm.jours_travail.length === jours.length ? [] : [...jours]})}
                  data-testid="jour-tous">Tous les jours</Badge>
                {jours.map(j => (
                  <Badge key={j} variant={assignForm.jours_travail.includes(j) ? "default" : "outline"} className={`cursor-pointer ${assignForm.jours_travail.includes(j) ? "bg-[#0F52BA]" : ""}`}
                    onClick={() => setAssignForm({...assignForm, jours_travail: assignForm.jours_travail.includes(j) ? assignForm.jours_travail.filter(d => d !== j) : [...assignForm.jours_travail, j]})}
                    data-testid={`jour-${j}`}>{j}</Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Jour non remunere</Label>
                <Select value={assignForm.jour_non_remunere} onValueChange={v => setAssignForm({...assignForm, jour_non_remunere: v})}>
                  <SelectTrigger data-testid="select-jour-free"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>{jours.map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Recette/jour (Frc)</Label><Input type="number" value={assignForm.recette_par_jour} onChange={e => setAssignForm({...assignForm, recette_par_jour: e.target.value})} required data-testid="input-recette-assign" /></div>
              <div><Label>Debut horaire</Label><Input type="time" value={assignForm.plage_horaire_debut} onChange={e => setAssignForm({...assignForm, plage_horaire_debut: e.target.value})} data-testid="input-horaire-debut" /></div>
              <div><Label>Fin horaire</Label><Input type="time" value={assignForm.plage_horaire_fin} onChange={e => setAssignForm({...assignForm, plage_horaire_fin: e.target.value})} data-testid="input-horaire-fin" /></div>
              <div><Label>Heure paiement</Label><Input type="time" value={assignForm.heure_paiement} onChange={e => setAssignForm({...assignForm, heure_paiement: e.target.value})} data-testid="input-heure-paiement" /></div>
              <div><Label>Date debut</Label><Input type="date" value={assignForm.date_debut} onChange={e => setAssignForm({...assignForm, date_debut: e.target.value})} required data-testid="input-date-debut" /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAssign(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-assign">Affecter</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Assignment Dialog */}
      <Dialog open={!!editAssignment} onOpenChange={() => setEditAssignment(null)}>
        <DialogContent data-testid="edit-assignment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Modifier l'affectation de {editAssignment ? getDriverName(editAssignment.chauffeur_id) : ""}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditAssignment} className="space-y-4">
            <div>
              <Label>Jours de travail</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant={editAssignForm.jours_travail.length === jours.length ? "default" : "outline"} className={`cursor-pointer ${editAssignForm.jours_travail.length === jours.length ? "bg-emerald-600" : ""}`}
                  onClick={() => setEditAssignForm({...editAssignForm, jours_travail: editAssignForm.jours_travail.length === jours.length ? [] : [...jours]})}
                  data-testid="edit-jour-tous">Tous les jours</Badge>
                {jours.map(j => (
                  <Badge key={j} variant={editAssignForm.jours_travail.includes(j) ? "default" : "outline"} className={`cursor-pointer ${editAssignForm.jours_travail.includes(j) ? "bg-[#0F52BA]" : ""}`}
                    onClick={() => setEditAssignForm({...editAssignForm, jours_travail: editAssignForm.jours_travail.includes(j) ? editAssignForm.jours_travail.filter(d => d !== j) : [...editAssignForm.jours_travail, j]})}
                    data-testid={`edit-jour-${j}`}>{j}</Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Jour non remunere</Label>
                <Select value={editAssignForm.jour_non_remunere} onValueChange={v => setEditAssignForm({...editAssignForm, jour_non_remunere: v})}>
                  <SelectTrigger data-testid="edit-select-jour-free"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aucun">Aucun</SelectItem>
                    {jours.map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Recette/jour (Frc)</Label><Input type="number" value={editAssignForm.recette_par_jour} onChange={e => setEditAssignForm({...editAssignForm, recette_par_jour: e.target.value})} required data-testid="edit-input-recette" /></div>
              <div><Label>Debut horaire</Label><Input type="time" value={editAssignForm.plage_horaire_debut} onChange={e => setEditAssignForm({...editAssignForm, plage_horaire_debut: e.target.value})} data-testid="edit-input-debut" /></div>
              <div><Label>Fin horaire</Label><Input type="time" value={editAssignForm.plage_horaire_fin} onChange={e => setEditAssignForm({...editAssignForm, plage_horaire_fin: e.target.value})} data-testid="edit-input-fin" /></div>
              <div><Label>Heure paiement</Label><Input type="time" value={editAssignForm.heure_paiement} onChange={e => setEditAssignForm({...editAssignForm, heure_paiement: e.target.value})} data-testid="edit-input-heure-paiement" /></div>
            </div>
            <div className="flex justify-between">
              <Button type="button" variant="destructive" size="sm" onClick={() => { setEditAssignment(null); handleDeleteAssignment(editAssignment.id); }} data-testid="btn-delete-from-edit">
                <Trash2 className="w-4 h-4 mr-1" />Supprimer l'affectation
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setEditAssignment(null)}>Annuler</Button>
                <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-edit-assign">Enregistrer</Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={!!showDocs} onOpenChange={() => setShowDocs(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Documents du vehicule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {Object.entries(DOC_LABELS).map(([docType, label]) => {
              const docs = vehicleDocs.filter(d => d.doc_type === docType);
              return (
                <div key={docType} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-semibold text-slate-700">{label}</Label>
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => handleDocUpload(showDocs, docType, e.target.files[0])} disabled={uploading} data-testid={`upload-${docType}`} />
                      <Button variant="outline" size="sm" asChild disabled={uploading}>
                        <span><Upload className="w-3.5 h-3.5 mr-1.5" />{uploading ? "..." : "Ajouter"}</span>
                      </Button>
                    </label>
                  </div>
                  {docs.length > 0 ? (
                    <div className="space-y-1.5">
                      {docs.map(d => (
                        <div key={d.id} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2 text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-[#0F52BA] shrink-0" />
                            <span className="truncate text-slate-700">{d.original_filename}</span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <a href={getDocUrl(d.storage_path)} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="icon" className="h-7 w-7"><Eye className="w-3.5 h-3.5 text-[#0F52BA]" /></Button>
                            </a>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDocDelete(showDocs, d.id)} data-testid={`delete-doc-${d.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">Aucun document</p>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
